import React, { useMemo, useEffect, useState } from 'react'
import { Stack, Grid, Box, Divider, Typography, Button, IconButton } from '@mui/material'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { Close, PhotoLibrary, AddAPhoto } from '@mui/icons-material'
import DatePicker from 'components/generic/form/DatePicker'
import noop from 'lodash/noop'
import { TransparentIconLabelButton } from 'components/generic/buttons/IconLabelButton'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import ScanIdModal from 'components/containers/customers/contacts/ScanIdModal'
import RequiredDocumentPreview from 'components/containers/customers/contacts/RequiredDocumentPreview'
import { formOptions } from 'utils/validation/complianceInfo'
import { ID_TYPES, GENDERS } from 'utils/constants'
import { getStatesAndProvinces } from 'utils/helpers'
import { saveRequiredDocumentsState } from 'state/customer/customerContactsSlice'
import { getComplianceData } from 'components/containers/customers/new-customer/utils'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'

const idTypesArray = [ID_TYPES.LICENSE]

const gendersArray = [GENDERS.F, GENDERS.M]

const ComplianceInformationForm = ({ data, onClose, onSave, fullView }) => {
  const dispatch = useDispatch()
  const [idModalIsOpen, setIdModalIsOpen] = useState(false)
  const { firstName, lastName } = data
  const fullName = `${firstName || ''} ${lastName || ''}`
  const defaultValues = getComplianceData(data)
  const partialView = !fullView
  const methods = useForm({ mode: 'all', defaultValues, ...formOptions })
  const { handleSubmit, control, formState, getValues } = methods
  const { errors } = formState
  const onSubmit = newValues => onSave({ ...data, ...newValues })
  const statesAndProvinces = useMemo(() => getStatesAndProvinces(['United States', 'Canada']), [])
  const {
    contact: { requiredDocuments: requiredDocumentsState },
  } = useSelector(state => state.customerContacts)
  const { settings } = useSelector(state => state.settings)
  const [initialRequiredDocuments, setInitialRequiredDocuments] = useState({})

  // store initial isTemp details when form opens, in case user clicks cancel
  useEffect(() => {
    setInitialRequiredDocuments(requiredDocumentsState)
  }, [])

  const requiredDocuments = [
    {
      id: 'frontOfID',
      name: 'Front of ID',
      action: {
        label: 'Change Photo',
        icon: <PhotoLibrary />,
        event: () => {
          setIdModalIsOpen(true)
        },
      },
      filenameKey: 'licenseImage',
    },
    {
      id: 'backOfID',
      name: 'Back of ID',
      action: {
        label: 'Change Photo',
        icon: <AddAPhoto />,
        event: () => {
          setIdModalIsOpen(true)
        },
      },
      filenameKey: 'licenseImageBack',
    },
  ]

  const handleCancel = () => {
    dispatch(saveRequiredDocumentsState(initialRequiredDocuments))
    onClose()
  }

  return (
    <Stack height="100vh">
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {fullView && (
          <Stack
            py={3}
            px={2}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ boxShadow: 'inset 0px -1px 0px #E0E0E0' }}
          >
            <Typography variant="h4" color="textPrimary">
              {fullName} | Compliance Information
            </Typography>
            <IconButton onClick={() => handleCancel()} size="large">
              <Close />
            </IconButton>
          </Stack>
        )}
        <Stack direction={fullView ? 'row' : 'column-reverse'} overflow={fullView ? 'scroll' : 'unset'} height="100%">
          <Box
            mt={fullView ? 0 : 4}
            pl={fullView ? 0 : 3}
            width={fullView ? '25%' : 'auto'}
            textAlign={fullView ? 'center' : 'left'}
            overflow={fullView ? 'scroll' : 'unset'}
            sx={{ bgcolor: 'info.lightBg' }}
          >
            <Typography variant="h6" color="textSecondary" pt={fullView ? 4 : 1} pb="3px">
              Required Documents
            </Typography>
            <Box py={1}>
              <Divider sx={{ width: fullView ? 'auto' : '218px' }} />
            </Box>
            <Stack
              direction={fullView ? 'column' : 'row'}
              justifyContent={fullView ? 'center' : 'space-around'}
              sx={{ pb: fullView ? 0 : 2 }}
              textAlign="center"
            >
              {requiredDocuments.map(document => (
                <RequiredDocumentPreview
                  key={document.id}
                  document={document}
                  image={{
                    filename: getValues(document.filenameKey),
                    isTemp: requiredDocumentsState[document.filenameKey]?.isTempFolder || false,
                  }}
                />
              ))}
            </Stack>
          </Box>
          <Stack overflow={partialView ? 'unset' : 'scroll'} ml={3} mr={4} pb={3}>
            <Stack direction="row" alignItems="end">
              <Typography variant="h6" color="textSecondary" pt={partialView ? 0 : 4} mr={3}>
                Personal Information
              </Typography>
              {fullView && (
                <Box>
                  <TransparentIconLabelButton
                    startIcon={<AddAPhoto />}
                    text="Scan ID"
                    onClick={() => {
                      setIdModalIsOpen(true)
                    }}
                  />
                </Box>
              )}
            </Stack>
            <Box pt={1}>
              <Divider />
            </Box>
            <Stack flexGrow={1}>
              <Grid container mt={1} spacing={2}>
                <Grid item md={6} xs={12}>
                  <Input
                    name="firstNameLegal"
                    label="First Name"
                    defaultValue=""
                    required
                    control={control}
                    error={!!errors.firstNameLegal}
                    helperText={errors.firstNameLegal?.message}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Input
                    name="lastNameLegal"
                    label="Last Name"
                    defaultValue=""
                    required
                    control={control}
                    error={!!errors.lastNameLegal}
                    helperText={errors.lastNameLegal?.message}
                  />
                </Grid>
              </Grid>
              <Grid container mt={1} spacing={2}>
                <Grid item md={6} xs={12}>
                  <Input
                    name="address1"
                    label="Address 1"
                    defaultValue=""
                    control={control}
                    error={!!errors.address1}
                    helperText={errors.address1?.message}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <Input
                    name="address2"
                    label="Address 2"
                    defaultValue=""
                    control={control}
                    error={!!errors.address2}
                    helperText={errors.address2?.message}
                  />
                </Grid>
              </Grid>
              <Grid container mt={1} spacing={2}>
                <Grid item md={6} xs={12}>
                  <Input
                    name="city"
                    label="City"
                    defaultValue=""
                    control={control}
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                </Grid>
                <Grid container item md={6} xs={12} spacing={2}>
                  <Grid item md={5} xs={6}>
                    <Select
                      name="state"
                      label="State"
                      defaultValue=""
                      control={control}
                      items={statesAndProvinces}
                      error={!!errors.state}
                      helperText={errors.state?.message}
                    />
                  </Grid>
                  <Grid item md={7} xs={6}>
                    <Input
                      name="zip"
                      label="Zip"
                      defaultValue=""
                      control={control}
                      error={!!errors.zip}
                      helperText={errors.zip?.message}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Stack>
            <Typography variant="h6" color="textSecondary" pt={4} mr={3}>
              ID Information
            </Typography>
            <Box pt={1}>
              <Divider />
            </Box>
            <Stack flexGrow={1}>
              <Grid container mt={1} spacing={2}>
                <Grid item md={12} xs={12}>
                  <Select
                    name="idType"
                    label="ID Type"
                    defaultValue=""
                    control={control}
                    items={idTypesArray}
                    error={!!errors.idType}
                    helperText={errors.idType?.message}
                  />
                </Grid>
              </Grid>
              <Grid container mt={1} spacing={2}>
                <Grid item md={4} xs={12}>
                  <Input
                    name="licenseNumber"
                    label="License Number"
                    defaultValue=""
                    control={control}
                    error={!!errors.licenseNumber}
                    helperText={errors.licenseNumber?.message}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <DatePicker
                    name="licenseExpiration"
                    label="License Expiration"
                    variant="outlined"
                    error={!!errors.licenseExpiration?.dateCreated}
                    helperText={errors.licenseExpiration?.dateCreated?.message}
                    control={control}
                  />
                </Grid>
                <Grid item md={4} xs={12}>
                  <DatePicker
                    name="birthDate"
                    label="Date of Birth"
                    variant="outlined"
                    error={!!errors.birthDate}
                    helperText={errors.birthDate?.message}
                    control={control}
                  />
                </Grid>
              </Grid>
              <Grid container mt={1} spacing={2}>
                <Grid item md={3} xs={6}>
                  <Input
                    name="height"
                    label="Height"
                    defaultValue=""
                    control={control}
                    error={!!errors.height}
                    helperText={errors.height?.message}
                  />
                </Grid>
                <Grid item md={3} xs={6}>
                  <Input
                    name="eyes"
                    label="Eye Color"
                    defaultValue=""
                    control={control}
                    error={!!errors.eyes}
                    helperText={errors.eyes?.message}
                  />
                </Grid>
                <Grid item md={3} xs={6}>
                  <Input
                    name="hair"
                    label="Hair Color"
                    defaultValue=""
                    control={control}
                    error={!!errors.hair}
                    helperText={errors.hair?.message}
                  />
                </Grid>
                <Grid item md={3} xs={6}>
                  <Select
                    name="gender"
                    label="Gender"
                    defaultValue=""
                    control={control}
                    items={gendersArray}
                    error={!!errors.gender}
                    helperText={errors.gender?.message}
                  />
                </Grid>
              </Grid>
              {settings.customerSettings.compliance.enableRaceAndWeightCollection && (
                <Grid container mt={1} spacing={2}>
                  <Grid item md={6} xs={12}>
                    <Input
                      name="race"
                      label="Race"
                      defaultValue=""
                      control={control}
                      error={!!errors.race}
                      helperText={errors.race?.message}
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Input
                      name="weight"
                      label="Weight"
                      defaultValue=""
                      control={control}
                      error={!!errors.weight}
                      helperText={errors.weight?.message}
                      InputProps={{
                        inputComponent: InputFormat,
                        inputProps: {
                          inputType: 'number',
                          thousandSeparator: false,
                          allowNegative: false,
                          decimalScale: 0,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Stack>
          </Stack>
        </Stack>
        {fullView && (
          <Stack direction="row" spacing={1} py={3} pl={4} sx={{ boxShadow: 'inset 0px 1px 0px #E0E0E0' }}>
            <Button type="submit" variant="contained">
              Save Information
            </Button>
            <Button variant="outlined" onClick={() => handleCancel()}>
              Cancel
            </Button>
          </Stack>
        )}
        <ScanIdModal
          isOpen={idModalIsOpen}
          methods={methods}
          onHideModal={() => {
            setIdModalIsOpen(false)
          }}
        />
      </form>
    </Stack>
  )
}

ComplianceInformationForm.propTypes = {
  data: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  fullView: PropTypes.bool,
}

ComplianceInformationForm.defaultProps = {
  data: {},
  onClose: noop,
  onSave: noop,
  fullView: true,
}

export default ComplianceInformationForm
